import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { User, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { auth } from "./firebase";

interface Resident {
  id: string;
  email: string;
  buildingIds: string[];
  residentId?: string;
  isActive: boolean;
}

interface AuthContextType {
  user: User | null;
  token: {
    uid: string;
    role: string;
    buildingIds: string[];
    residentId?: string;
    locationId?: string;
  } | null;
  buildinggId: string | null;
  residentId?: string;
  loading: boolean;
  unauthorized: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  selectBuildingId: (buildinggId: string | null, tokenResult?: any) => boolean;
  resident: Resident | null;
  setResident: (resident: Resident | null) => void;
}

interface ResidentToken {
  uid: string;
  role: string;
  buildingIds: string[];
  residentId?: string;
  locationId?: string;
}

function normalizeBuildingIds(buildingIds: unknown, locationId?: string): string[] {
  const normalized = Array.isArray(buildingIds)
    ? buildingIds.filter((value): value is string => typeof value === "string" && value.length > 0)
    : [];

  if (locationId && !normalized.includes(locationId)) {
    normalized.push(locationId);
  }

  return normalized;
}

function buildToken(user: User, claims: Record<string, unknown>): ResidentToken {
  const locationId = typeof claims.locationId === "string" ? claims.locationId : undefined;

  return {
    uid: user.uid,
    role: typeof claims.role === "string" ? claims.role : "",
    buildingIds: normalizeBuildingIds(claims.buildingIds, locationId),
    residentId: typeof claims.residentId === "string" ? claims.residentId : undefined,
    locationId,
  };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);

  // Log when user state changes
  useEffect(() => {
    console.log("AUTH_SET_USER", user?.uid ?? null);
  }, [user]);
  const [token, setToken] = useState<AuthContextType["token"] | null>(null);
  const [buildinggId, setBuildingId] = useState<string | null>(null);
  const [unauthorized, setUnauthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resident, setResident] = useState<Resident | null>(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      console.log("AUTH_STATE_CHANGED", firebaseUser?.uid ?? null);
      setLoading(true);

      // CRITICAL: Set user state immediately when auth state changes
      setUser(firebaseUser);
      console.log("AUTH_SET_USER_FROM_STATE_CHANGED", firebaseUser?.uid ?? null);

      try {
        if (firebaseUser) {
          const idTokenResult = await firebaseUser.getIdTokenResult();
          const claims = idTokenResult.claims || {};
          const parsedToken = buildToken(firebaseUser, claims);
          setToken(parsedToken);

          const savedBuildingId = localStorage.getItem("resident_buildinggId");
          if (savedBuildingId) {
            if (parsedToken.role?.toUpperCase() === "RESIDENT" && parsedToken.locationId === savedBuildingId) {
              setBuildingId(savedBuildingId);
            } else if (parsedToken.buildingIds.includes(savedBuildingId)) {
              setBuildingId(savedBuildingId);
            } else if (parsedToken.role?.toUpperCase() === "RESIDENT" && parsedToken.locationId) {
              setBuildingId(parsedToken.locationId);
              localStorage.setItem("resident_buildinggId", parsedToken.locationId);
            }
          } else if (parsedToken.role?.toUpperCase() === "RESIDENT" && parsedToken.locationId) {
            // Auto-set buildinggId for residents from locationId claim
            setBuildingId(parsedToken.locationId);
            localStorage.setItem("resident_buildinggId", parsedToken.locationId);
            console.log("BOOTSTRAP_BUILDING_SET", { role: parsedToken.role, locationId: parsedToken.locationId });
          }
        } else {
          // User signed out - reset state
          console.log("AUTH_USER_SIGNED_OUT");
          setToken(null);
          setBuildingId(null);
          setResident(null);
          setUnauthorized(false);
        }
      } catch (error) {
        console.error("AUTH_BOOTSTRAP_ERROR", error);
        setToken(null);
        setBuildingId(null);
        setResident(null);
        setUnauthorized(true);
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    console.log("LOGIN_START", { email });
    if (loading) {
      console.log("LOGIN_SKIP: Already logging in or loading");
      return { success: false, error: "Login in progress. Please wait." };
    }
    setLoading(true);
    setUnauthorized(false);
    try {
      console.log("LOGIN_AUTH_CALL");
      const credential = await signInWithEmailAndPassword(auth, email, password);
      console.log("LOGIN_AUTH_SUCCESS", { uid: credential.user.uid, email: credential.user.email });

      // CRITICAL: Set user state immediately after successful authentication
      setUser(credential.user);
      console.log("AUTH_SET_USER_DIRECT", credential.user.uid);

      // Force token refresh to get latest claims
      console.log("LOGIN_TOKEN_REFRESH");
      await credential.user.getIdToken(true);
      console.log("LOGIN_GETTING_TOKEN_RESULT");
      const tokenResult = await credential.user.getIdTokenResult();
      console.log("LOGIN_TOKEN_RESULT_OK");
      const claims = tokenResult.claims || {};
      console.log("LOGIN_CLAIMS", JSON.stringify(claims));

      const parsedToken = buildToken(credential.user, claims);
      const { role, residentId, locationId, buildingIds } = parsedToken;

      console.log("LOGIN_CLAIM_EXTRACT", { role, residentId, locationId, buildingIds });

      // Verify role matches expected case for Firestore rules
      if (role?.toUpperCase() === "RESIDENT") {
        console.log("LOGIN_ROLE_RESIDENT");
        if (!residentId || !locationId) {
          console.log("LOGIN_ERROR: Missing residentId or locationId claim");
          setLoading(false);
          return { success: false, error: "No resident account found. Please contact concierge." };
        }

        // Resident login: use claims only - no Firestore reads
        console.log("LOGIN_SETTING_TOKEN");
        setToken(parsedToken);
        console.log("LOGIN_TOKEN_SET_DONE");

        // Auto-set buildinggId from locationId claim
        console.log("LOGIN_SETTING_BUILDING_ID");
        setBuildingId(locationId);
        localStorage.setItem("resident_buildinggId", locationId);
        console.log("LOGIN_BUILDING_SET", { buildinggId: locationId });

        // Set minimal resident object from claims only - NO Firestore read of residents collection
        console.log("LOGIN_SETTING_RESIDENT_FROM_CLAIMS_ONLY");
        setResident({
          id: residentId,
          email: credential.user.email || "",
          buildingIds,
          residentId,
          isActive: true, // Assuming active if claims are present
        });
        console.log("LOGIN_RESIDENT_SET_DONE");

        console.log("POST_LOGIN_STATE_BEFORE", {
          authUid: credential.user.uid,
          residentId,
          locationId: claims.locationId,
        });
        console.log("LOGIN_SUCCESS_NO_DB_READ", { uid: credential.user.uid, residentId, locationId });
        setLoading(false);
        console.log("LOGIN_LOADING_FALSE");
        return { success: true };
      }

      if (role) {
        console.log("LOGIN_ERROR: Non-resident attempted resident portal login", { role });
        setLoading(false);
        return { success: false, error: "This account does not have Resident Portal access." };
      }

      // Staff login path
      if (!buildingIds || buildingIds.length === 0) {
        console.log("LOGIN_ERROR: No buildingIds for staff");
        setLoading(false);
        return { success: false, error: "No access configured. Please contact administrator." };
      }

      console.log("LOGIN_SETTING_TOKEN_STAFF");
      setToken({
        uid: credential.user.uid,
        role,
        buildingIds,
      });
      console.log("LOGIN_TOKEN_STAFF_SET_DONE");

      console.log("POST_LOGIN_STATE_BEFORE", {
        authUid: credential.user.uid,
        buildingIds,
      });
      console.log("LOGIN_SUCCESS_STAFF", { uid: credential.user.uid, buildingIds });
      setLoading(false);
      console.log("LOGIN_LOADING_FALSE_STAFF");
      return { success: true };
    } catch (error: any) {
      console.error("LOGIN_ERROR", error);
      console.error("LOGIN_ERROR_CODE", error.code);
      console.error("LOGIN_ERROR_MESSAGE", error.message);
      console.error("LOGIN_ERROR_STACK", error.stack);
      setLoading(false);
      const message = error.message || "Login failed";
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem("resident_buildinggId");
      setUser(null);
      setToken(null);
      setBuildingId(null);
      setUnauthorized(false);
      setResident(null);
    } catch (error: any) {
      console.error("Logout error:", error);
    }
  };

  const selectBuildingId = (buildinggIdParam: string | null, tokenResult?: any) => {
    const claims = tokenResult?.claims ?? token;

    // If token has multiple buildings (buildingIds array), use locationId from token directly
    // Otherwise, check if locationId matches buildinggId
    let targetLocationId = claims.locationId ?? "";

    if (claims.buildingIds && Array.isArray(claims.buildingIds)) {
      // User has multiple buildings - check if requested buildinggId is in their list
      const locationIndex = claims.buildingIds.indexOf(targetLocationId);
      if (locationIndex === -1) {
        targetLocationId = targetLocationId;
      }
    }

    const authorized =
      claims?.role?.toUpperCase() === "RESIDENT"
        ? targetLocationId === claims.locationId
        : targetLocationId !== "" && claims.buildingIds?.includes(targetLocationId);

    console.log("AUTHZ_CHECK", {
      role: claims?.role,
      residentId: claims?.residentId,
      claimsLocationId: claims?.locationId,
      targetLocationId,
      buildingIds: claims?.buildingIds,
      authorized,
    });

    if (!authorized) {
      console.error("User not authorized for this building");
      setUnauthorized(true);
      return false;
    }

    if (targetLocationId) {
      setBuildingId(targetLocationId);
      localStorage.setItem("resident_buildinggId", targetLocationId);
      setUnauthorized(false);
    }

    return true;
  };

  const value: AuthContextType = {
    user,
    token,
    buildinggId,
    unauthorized,
    loading,
    login,
    logout,
    selectBuildingId,
    resident,
    setResident,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
